// ActivationLabelExport.js — customer-scoped provisioning modal.
// Confirm-before-generate (no secret rotation on open), provisioned-aware warning,
// per-label PNG + batch PDF, auto-on-download print tracking, unsaved-labels close guard.
// autoGenerate: the replacement-label flow after unregister / regenerate-key (both revoke the
// label server side). The admin already confirmed that action, so it mints on open with no
// second confirmation; a failed label offers Retry (the licence stays unregistered meanwhile).
import React, { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import { useAuth } from 'react-oidc-context';
import { licenseService } from './apiService';
import { generateActivationQRJSON, isActivationLabelEnvironment } from './qrCodeUtils';
import { Modal } from './components/Modal';
import { Button } from './components/Button';
import './ActivationLabelExport.css';

const CONCURRENCY = 5;

const CONFIRM_TITLE_ID = 'activation-confirm-title';
const LABELS_TITLE_ID = 'activation-labels-title';
const GUARD_TITLE_ID = 'activation-guard-title';

// A minted secret whose environment is not exactly dev or prod is a failed label: the app
// would refuse it, so it is never rendered or downloaded (Retry stays available).
const toResult = (license, settled) => {
  if (settled.status === 'fulfilled' && !isActivationLabelEnvironment(settled.value?.environment)) {
    return { license, status: 'rejected', data: null, invalidEnvironment: true };
  }
  return {
    license,
    status: settled.status,
    data: settled.status === 'fulfilled' ? settled.value : null,
  };
};

const ActivationLabelExport = ({ licenses, onClose, autoGenerate = false, notice = null, failureNotice = null }) => {
  const auth = useAuth();
  const token = auth.user?.access_token;
  const [generated, setGenerated] = useState(autoGenerate);
  const [working, setWorking] = useState(false);
  const [results, setResults] = useState([]);
  const [retrying, setRetrying] = useState({});
  const [downloaded, setDownloaded] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  // Every mint rotates the secret, so the auto mint must run exactly once per open
  // (StrictMode re-runs effects in development; the ref survives that re-run).
  const autoStarted = useRef(false);

  const total = licenses?.length || 0;
  const provisionedCount = (licenses || []).filter((l) => l.activationSecretSetAt).length;

  async function handleGenerate() {
    if (total === 0) return;
    // No access token: record every label as failed so the failure message and Retry show.
    if (!token) {
      setResults(licenses.map((lic) => toResult(lic, { status: 'rejected' })));
      setGenerated(true);
      return;
    }
    setWorking(true);
    const out = [];
    for (let i = 0; i < licenses.length; i += CONCURRENCY) {
      const batch = licenses.slice(i, i + CONCURRENCY);
      const settled = await Promise.allSettled(
        batch.map((lic) => licenseService.generateActivationSecret(lic.serialNumber, token)),
      );
      out.push(...settled.map((r, idx) => toResult(batch[idx], r)));
    }
    setResults(out);
    setGenerated(true);
    setWorking(false);
  }

  useEffect(() => {
    if (!autoGenerate || autoStarted.current) return;
    autoStarted.current = true;
    handleGenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerate]);

  async function handleRetry(index) {
    const { license } = results[index];
    setRetrying((prev) => ({ ...prev, [license.serialNumber]: true }));
    const [settled] = await Promise.allSettled([
      token
        ? licenseService.generateActivationSecret(license.serialNumber, token)
        : Promise.reject(new Error('Not signed in')),
    ]);
    setResults((prev) => prev.map((r, i) => (i === index ? toResult(license, settled) : r)));
    setRetrying((prev) => ({ ...prev, [license.serialNumber]: false }));
  }

  async function markPrinted(serialNumbers) {
    setDownloaded(true);
    await Promise.allSettled(
      serialNumbers.map((sn) => licenseService.setOnboardingStatus(sn, { labelPrinted: true }, token)),
    );
  }

  function handleDownloadPNG(result, index) {
    const canvas = document.getElementById(`activation-qr-${index}`);
    if (!canvas || !canvas.toDataURL) { markPrinted([result.license.serialNumber]); return; }
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `activation-${result.license.serialNumber}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    markPrinted([result.license.serialNumber]);
  }

  async function handleDownloadPDF() {
    const ok = results.filter((r) => r.status === 'fulfilled');
    if (ok.length === 0) return;
    const QRCode = require('qrcode');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const qrSize = 50; const margin = 12; let y = 20;
    pdf.setFontSize(14); pdf.text('Activation Labels', margin, 12);
    for (const r of ok) {
      const value = generateActivationQRJSON(r.license.serialNumber, r.data.activationSecret, r.data.environment);
      const canvas = document.createElement('canvas');
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res, rej) => QRCode.toCanvas(canvas, value, { width: 200, margin: 2, errorCorrectionLevel: 'H' }, (e) => (e ? rej(e) : res())));
      if (y + qrSize + 30 > pdf.internal.pageSize.getHeight()) { pdf.addPage(); y = 20; }
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, y, qrSize, qrSize);
      pdf.setFontSize(9);
      let ty = y + qrSize + 5;
      pdf.text(`Serial #: ${r.license.serialNumber}`, margin, ty); ty += 4;
      if (r.license.customerName) { pdf.text(`Customer: ${r.license.customerName}`, margin, ty); ty += 4; }
      pdf.text('Secret shown once · does not expire', margin, ty);
      y += qrSize + 30;
    }
    pdf.save('activation-labels.pdf');
    markPrinted(ok.map((r) => r.license.serialNumber));
  }

  const anyGenerated = results.some((r) => r.status === 'fulfilled');
  const anyFailed = results.some((r) => r.status !== 'fulfilled');
  // Minting or retrying: closing now would discard a secret the server has already rotated.
  const busy = working || Object.values(retrying).some(Boolean);

  // Auto mode: one always-present polite live region whose text follows the mint.
  const autoStatus = busy ? { text: 'Creating the new activation label…', className: undefined }
    : anyFailed ? { text: failureNotice, className: 'activation-labels__alert' }
      : anyGenerated ? { text: notice, className: 'activation-labels__notice' }
        : { text: '', className: undefined };

  function requestClose() {
    if (anyGenerated && !downloaded) { setConfirmingClose(true); return; }
    onClose();
  }

  // --- Confirmation screen (no API call) ---
  if (!generated) {
    return (
      <Modal isOpen onClose={onClose} labelledBy={CONFIRM_TITLE_ID} dismissable={!working}>
        <Modal.Header id={CONFIRM_TITLE_ID}>Generate activation labels</Modal.Header>
        <Modal.Body>
          <p>
            Generate activation labels for {total} device(s)?
            {provisionedCount > 0 && (
              <> <strong>{provisionedCount} of {total} are already provisioned</strong> — generating rotates each secret, <strong>invalidates any previously printed label</strong>, and <strong>resets their Printed/Shipped tracking back to "Generated"</strong> (per the backend rotation reset). Already-<em>activated</em> devices stay activated.</>
            )} The secret is shown once — print before closing.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={onClose} disabled={working}>Cancel</Button>
          <Button variant="primary" onClick={handleGenerate} loading={working} disabled={total === 0}>Generate</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // --- Unsaved-labels close guard ---
  // Rendered as a content swap rather than a stacked modal: two simultaneously
  // active focus traps make the inner dialog's clicks unreachable (the outer
  // trap stops propagation on clicks it treats as outside). Label state is
  // preserved, so "Keep labels open" returns to the intact grid.
  if (confirmingClose) {
    return (
      <Modal isOpen onClose={() => setConfirmingClose(false)} labelledBy={GUARD_TITLE_ID}>
        <Modal.Header id={GUARD_TITLE_ID}>Unsaved activation labels</Modal.Header>
        <Modal.Body>
          <p>These activation labels have not been downloaded. Closing loses the secrets, and regenerating will rotate them. Close anyway?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setConfirmingClose(false)}>Keep labels open</Button>
          <Button variant="danger" onClick={onClose}>Close anyway</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // --- Label grid ---
  return (
    <Modal isOpen onClose={requestClose} labelledBy={LABELS_TITLE_ID} size="lg" dismissable={!busy}>
      <Modal.Header id={LABELS_TITLE_ID}>Activation Labels</Modal.Header>
      <Modal.Body>
        {autoGenerate && (
          <p role="status" aria-live="polite" className={autoStatus.className}>
            {autoStatus.text && <strong>{autoStatus.text}</strong>}
          </p>
        )}
        <p><strong>These secrets are shown only once.</strong> Affix the label to the device; the operator scans it to activate. Labels do not expire.</p>
        {anyGenerated && (
          <div className="activation-labels__actions">
            <Button variant="primary" onClick={handleDownloadPDF}>Download all (PDF)</Button>
          </div>
        )}
        <div className="activation-labels__grid">
          {results.map((r, i) => (
            <div key={r.license.serialNumber} className="activation-labels__card">
              <p className="activation-labels__serial">{r.license.serialNumber}</p>
              {r.status === 'fulfilled' ? (
                <>
                  <QRCodeCanvas id={`activation-qr-${i}`} value={generateActivationQRJSON(r.license.serialNumber, r.data.activationSecret, r.data.environment)} size={180} level="H" />
                  {[r.license.customerName, r.license.customerNumber, r.license.country].filter(Boolean).map((v, k) => <p key={k} className="activation-labels__meta">{v}</p>)}
                  <p className={r.data.rotated ? 'activation-labels__status activation-labels__status--warn' : 'activation-labels__status activation-labels__status--info'}>
                    {r.data.rotated ? 'Rotated — prior label invalidated' : 'New'}
                  </p>
                  <Button variant="ghost" size="sm" onClick={() => handleDownloadPNG(r, i)}>Download PNG</Button>
                </>
              ) : (
                <>
                  <p className="activation-labels__status activation-labels__status--error">
                    {r.invalidEnvironment ? 'Failed to generate label: server stage is not dev or prod' : 'Failed to generate label'}
                  </p>
                  <Button variant="primary" size="sm" onClick={() => handleRetry(i)} loading={Boolean(retrying[r.license.serialNumber])}>Retry</Button>
                </>
              )}
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={requestClose} disabled={busy}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ActivationLabelExport;
